import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import extractFlightData from 'legacyfrontend/helpers/extractFlightPricingData';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import {
  getFlightSearchState,
  updateFlightSearchState,
  clearFlightSearchState,
} from 'legacyfrontend/helpers/flightSearchStateStorage';
import { clearFlightSelectionStateForNewSearch as clearStoredFlightSelectionStateForNewSearch } from 'legacyfrontend/flights/search/flightSearchStorage';
import { prepareEmailData } from 'legacyfrontend/helpers/prepareEmailData';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PackagesApi from '../api/paquetes';
import { CACHE_DEFAULTS } from '../constants';

const FlightDetailsContext = createContext();

export const FlightProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const agency = useSelector((state) => state.applicationState.templateData.header);
  const principalOffice = useSelector(
    (state) => state.applicationState.templateData.principalOffice
  );
  const tokenMarcaBlanca = useSelector((state) => state.applicationState.token);
  const { emailContacto: emailAgency, empresa: nameAgency, telefonoContacto: agencyPhone } = agency;

  const savedState = useMemo(() => getFlightSearchState(), []);

  const [flightSelections, setFlightSelections] = useState(savedState.flightSelections ?? {});
  const [isOnlyOneWay, setIsOnlyOneWay] = useState(savedState.isOnlyOneWay ?? false);
  const [selectedFare, setSelectedFare] = useState(savedState.selectedFare ?? null);
  const [flightInfo, setFlightInfo] = useState(savedState.flightInfo ?? null);
  const [expCacheDate, setExpCacheDate] = useState(savedState.expCacheDate ?? null);

  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleFlightDetails = async (cardId) => {
    const selectedFlights = flightSelections[cardId];
    if (
      (isOnlyOneWay && !selectedFlights?.ida) ||
      (!isOnlyOneWay && (!selectedFlights?.vuelta || !selectedFlights?.ida))
    ) {
      return;
    }

    const cacheValue = getCacheStatus();

    try {
      const data = {
        token: selectedFlights.ida.token,
        solutionID0: selectedFlights.ida.solutionId,
        journey00: selectedFlights.ida.journey,
        journey01: isOnlyOneWay ? selectedFlights.ida.journey : selectedFlights.vuelta.journey,
        cache: cacheValue,
        originId: flightInfo.originId,
        destinationID: flightInfo.destinationId,
        passengers: flightInfo.passengers,
        cabin: flightInfo.cabinClass ?? 'Y',
        currency: flightInfo.currency,
      };

      const response = await PackagesApi.getLlegoFlightDetails(data);
      return { success: true, data: response.data };
    } catch (error) {
      showError(error?.message);
      if (error?.response?.status === 400) {
        return { success: false, error };
      }
      throw error;
    }
  };

  const lastCardId = useMemo(() => {
    return Object.keys(flightSelections)[Object.keys(flightSelections).length - 1] || null;
  }, [flightSelections]);

  const handlePricingAndStandalone = async () => {
    if (!lastCardId) return;
    const selectedFlights = flightSelections[lastCardId];
    if (!selectedFlights?.ida || (!isOnlyOneWay && !selectedFlights?.vuelta)) {
      return;
    }

    const cacheValue = getCacheStatus();

    try {
      const baseData = {
        token: selectedFlights.ida.token,
        solutionID0: selectedFare.id,
        journey00: selectedFare.journeys_ids[0],
        journey01: isOnlyOneWay ? selectedFare.journeys_ids[0] : selectedFare.journeys_ids[1],
        cache: cacheValue,
      };

      const pricingData = {
        ...baseData,
        originId: flightInfo.originId,
        destinationID: flightInfo.destinationId,
        passengers: flightInfo.passengers,
        cabin: flightInfo.cabinClass ?? 'Y',
        currency: flightInfo.currency,
      };

      setFlightInfo((prevInfo) => {
        const updatedInfo = { ...(prevInfo || {}), ...baseData };
        return updatedInfo;
      });

      const [pricingResponse, standaloneResponse] = await Promise.all([
        PackagesApi.getLlegoPricing(pricingData),
        PackagesApi.getLlegoStandalone(baseData),
      ]);

      return {
        success: true,
        pricing: extractFlightData(pricingResponse),
        standalone: standaloneResponse,
      };
    } catch (error) {
      showError(error?.message);
      if (error?.response?.status === 400) {
        return { success: false, error };
      }
      throw error;
    }
  };

  const createBooking = async (bookingModel) => {
    setIsLoading(true);
    setErrorState(null);

    try {
      const token = await PackagesApi.getToken();

      const bookingModelWithAgency = {
        ...bookingModel,
        nameAgency,
        emailAgency,
        agencyPhone,
      };
      const response = await PackagesApi.createBooking(bookingModelWithAgency, token);

      if (!response?.success) {
        if (response?.booking?.id) {
          return {
            success: true,
            data: response,
          };
        }

        return {
          success: false,
          errors: response?.errors,
        };
      }

      return {
        success: true,
        data: response,
      };
    } catch (err) {
      console.error('Error en createBooking:', err);
      setErrorState(err || 'No se pudo crear la reserva, intente nuevamente o contacte a soporte');
      return {
        success: false,
        error: err || 'No se pudo crear la reserva, intente nuevamente o contacte a soporte',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailInfo = async (
    contactInfo,
    bookingData = null,
    pricingData = null,
    apiBookingResponse = null,
    extraBaggage = {}
  ) => {
    setIsLoading(true);
    setErrorState(null);
    const emailData = {
      emailAgency,
      email: contactInfo.email,
      nameAgency,
      agencyAddress: principalOffice?.direccion,
      agencyPhone,
      name: contactInfo?.name,
      surnames: contactInfo?.surnames,
      phone: contactInfo?.telefono1?.numero,
    };

    const finalEmailData = prepareEmailData(
      emailData,
      bookingData,
      pricingData,
      apiBookingResponse,
      extraBaggage
    );

    try {
      const result = await PackagesApi.sendMail(finalEmailData, tokenMarcaBlanca);
      return {
        success: true,
        data: result,
      };
    } catch (err) {
      console.error('Error en sendEmail:', err);
      setErrorState(err || 'No se pudo enviar el mail, intente nuevamente o contacte a soporte');
      return {
        success: false,
        error: err || 'No se pudo enviar el mail, intente nuevamente o contacte a soporte',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const setOneWayFlight = useCallback((value) => {
    const normalizedValue = typeof value === 'string'
      ? value === 'true'
      : Boolean(value);

    setIsOnlyOneWay((prevValue) => {
      if (normalizedValue === prevValue) {
        return prevValue;
      }

      try {
        updateFlightSearchState({ isOnlyOneWay: normalizedValue });
        setFlightInfo((prevInfo) => {
          if (prevInfo && prevInfo.isOnlyOneWay !== normalizedValue) {
            return { ...prevInfo, isOnlyOneWay: normalizedValue };
          }
          return prevInfo;
        });
      } catch (error) {
        console.warn('Error saving isOnlyOneWay to flightSearchState:', error);
      }

      return normalizedValue;
    });
  }, [flightInfo]);

  const selectFlight = useCallback((cardId, flight, type) => {
    setFlightSelections((prev) => {
      const newSelections = {
        ...prev,
        [cardId]: {
          ...prev[cardId],
          [type]: flight,
        },
      };
      try {
        updateFlightSearchState({ flightSelections: newSelections });
      } catch (error) {
        console.warn('Error saving flight selections to flightSearchState:', error);
      }
      return newSelections;
    });
  }, []);

  const selectFare = useCallback((fareData) => {
    setSelectedFare(fareData);
    try {
      updateFlightSearchState({ selectedFare: fareData });
    } catch (error) {
      console.warn('Error saving fare to flightSearchState:', error);
    }
  }, []);

  const setFlightInformation = useCallback((info) => {
    setFlightInfo((prevInfo) => {
      const newInfo = typeof info === 'function' ? info(prevInfo) : info;
      
      if (typeof info === 'function' && newInfo == null) {
        return prevInfo;
      }
      
      const updatedInfo = { ...(prevInfo || {}), ...newInfo };

      if (JSON.stringify(updatedInfo) !== JSON.stringify(prevInfo)) {
        return updatedInfo;
      }
      return prevInfo;
    });
  }, []);

  const cleanSessionStorage = useCallback(() => {
    try {
      clearFlightSearchState();
      setFlightSelections({});
      setIsOnlyOneWay(false);
      setSelectedFare(null);
      setFlightInfo(null);
    } catch (error) {
      console.warn('Error cleaning flightSearchState:', error);
    }
  }, []);

  const clearFlightSelectionStateForNewSearch = useCallback((nextFlightInfo) => {
    try {
      const nextState = clearStoredFlightSelectionStateForNewSearch(nextFlightInfo);
      setFlightSelections({});
      setSelectedFare(null);
      setExpCacheDate(null);

      if (nextState?.flightInfo) {
        setFlightInfo(nextState.flightInfo);
      }
    } catch (error) {
      console.warn('Error clearing flight selection state for new search:', error);
    }
  }, []);

  const handleCacheExpiration = useCallback((apiExpirationDate) => {
    const expirationDate =
      apiExpirationDate || new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME);
    setExpCacheDate(expirationDate);
  }, []);

  useEffect(() => {
    if (flightInfo?.isOnlyOneWay !== undefined && flightInfo.isOnlyOneWay !== isOnlyOneWay) {
      setIsOnlyOneWay(flightInfo.isOnlyOneWay);
      try {
        updateFlightSearchState({ isOnlyOneWay: flightInfo.isOnlyOneWay });
      } catch (error) {
        console.warn('Error syncing isOnlyOneWay from flightInfo:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (expCacheDate) {
      try {
        updateFlightSearchState({ expCacheDate });
      } catch (error) {
        console.warn('Error saving expCacheDate to flightSearchState:', error);
      }
    }
  }, [expCacheDate]);

  useEffect(() => {
    if (flightInfo) {
      try {
        const stored = getFlightSearchState();
        const parsedSavedFlightInfo = stored.flightInfo;

        if (parsedSavedFlightInfo) {
          const updatedInfo = {
            ...parsedSavedFlightInfo,
            ...flightInfo,
            originCountryCode: parsedSavedFlightInfo.originCountryCode,
            destinationCountryCode: parsedSavedFlightInfo.destinationCountryCode,
            origin: {
              ...parsedSavedFlightInfo.origin,
              ...flightInfo.origin,
              countryCode: parsedSavedFlightInfo.origin?.countryCode,
            },
            destination: {
              ...parsedSavedFlightInfo.destination,
              ...flightInfo.destination,
              countryCode: parsedSavedFlightInfo.destination?.countryCode,
            },
          };

          if (JSON.stringify(updatedInfo) !== JSON.stringify(parsedSavedFlightInfo)) {
            updateFlightSearchState({ flightInfo: updatedInfo });
          }
        } else {
          updateFlightSearchState({ flightInfo });
        }
      } catch (error) {
        console.warn('Error saving flight info to flightSearchState:', error);
      }
    }
  }, [flightInfo]);

  const value = useMemo(
    () => ({
      flightSelections,
      selectFlight,
      handleFlightDetails,
      handlePricingAndStandalone,
      selectedFare,
      selectFare,
      flightInfo,
      setFlightInformation,
      isOnlyOneWay,
      setOneWayFlight,
      createBooking,
      sendEmailInfo,
      isLoading,
      setIsLoading,
      error: errorState,
      cleanSessionStorage,
      clearFlightSelectionStateForNewSearch,
      expCacheDate,
      setExpCacheDate,
      handleCacheExpiration,
    }),
    [
      flightSelections,
      selectFlight,
      handleFlightDetails,
      handlePricingAndStandalone,
      selectedFare,
      selectFare,
      flightInfo,
      setFlightInformation,
      isOnlyOneWay,
      setOneWayFlight,
      createBooking,
      sendEmailInfo,
      isLoading,
      setIsLoading,
      errorState,
      cleanSessionStorage,
      clearFlightSelectionStateForNewSearch,
      expCacheDate,
      handleCacheExpiration,
    ]
  );

  return (
    <FlightDetailsContext.Provider value={value}>
      {children}
      <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
    </FlightDetailsContext.Provider>
  );
};

export const useFlightContext = () => {
  const context = useContext(FlightDetailsContext);
  if (!context) {
    throw new Error('useFlightContext must be used within FlightProvider');
  }
  return context;
};
